/**
 * "Who is this person?" — the biography behind the long-press card.
 *
 * Keyed by `PersonId` and kept out of `people.ts` on purpose: people are a flat
 * registry referenced by id, and a 166-entry prose table inside those `p()`
 * calls would drown the stats they exist to hold.
 *
 * Every person in `people.ts` has an entry today. Anyone added later without
 * one falls back to `derivedBio`, which invents nothing — it is assembled from
 * data already in `people.ts` and `cohorts.ts`. The card is thinner for them,
 * never broken, and never says anything about a real person that isn't already
 * in the game data.
 *
 * These are real people, and most of them are alive. Only write what you
 * actually know: `years` is optional precisely so a role with uncertain dates
 * can ship as org + title, and the derived fallback exists so that "I'm not
 * sure" has somewhere to go that isn't a guess. Convictions and collapses are
 * stated plainly and only where they are a matter of public record.
 */

import { affinityFor } from './scoring';
import { CORE_PILLARS, ROLE_LABELS, ROLES, type Cohort, type Person, type PersonId } from './types';

export type Experience = {
  org: string;
  role: string;
  /** Omitted when the dates aren't known — better a missing line than a guess. */
  years?: string;
  /** Optional one-liner under the row, LinkedIn-description style. */
  note?: string;
};

export type PersonBio = {
  /** LinkedIn headline — the line right under the name. */
  headline: string;
  /** Omitted when we don't actually know it; the card just drops the line. */
  location?: string;
  /** "Why you've heard of them", 1–2 sentences. */
  about: string;
  experience: Experience[];
  /** The single fact worth remembering, for the compact variants. */
  claimToFame: string;
};

const BIOS: Partial<Record<PersonId, PersonBio>> = {
  // ── Check Point · 1990s ───────────────────────────────────────────────
  'gil-shwed': {
    headline: 'Co-founder & CEO at Check Point Software',
    location: 'Tel Aviv, Israel',
    about:
      'Wrote the patent for stateful inspection — the technique behind essentially every network firewall since. Built it at 24, out of his grandmother\'s apartment, and then ran the company for thirty years.',
    claimToFame: 'Patented the modern firewall at 24',
    experience: [
      { org: 'Check Point Software', role: 'Co-founder & CEO', years: '1993–2024', note: 'From 3 people to a $20B security company' },
      { org: 'Unit 8200, IDF', role: 'Software engineer', years: '1988–1991' },
    ],
  },
  'marius-nacht': {
    headline: 'Co-founder at Check Point · Founder at aMoon',
    location: 'Tel Aviv, Israel',
    about:
      'The co-founder who never wanted the microphone. Ran the business side of Check Point for two decades, then put the proceeds into aMoon, one of the largest health-tech funds in the world.',
    claimToFame: 'Built the empire and stayed off the stage',
    experience: [
      { org: 'aMoon Fund', role: 'Founder & Chairman', years: '2017–', note: '$1B+ health-tech fund' },
      { org: 'Check Point Software', role: 'Co-founder & Vice Chairman', years: '1993–2018' },
    ],
  },
  'shlomo-kramer': {
    headline: 'Co-founder at Cato Networks · Co-founder at Check Point & Imperva',
    location: 'Tel Aviv, Israel',
    about:
      'Founded three separate billion-dollar security companies. Most people manage zero. Investors call him back before he finishes the sentence.',
    claimToFame: 'Three unicorns, three categories, one career',
    experience: [
      { org: 'Cato Networks', role: 'Co-founder & CEO', years: '2015–' },
      { org: 'Imperva', role: 'Co-founder & CEO', years: '2002–2014', note: 'IPO 2011, acquired for $2.1B' },
      { org: 'Check Point Software', role: 'Co-founder', years: '1993–1998' },
    ],
  },
  'dorit-dor': {
    headline: 'Chief Technology Officer at Check Point Software',
    location: 'Tel Aviv, Israel',
    about:
      'Runs both product and threat research at a company whose entire value is being right about attacks before they happen. PhD in computer science; won the Israel Defense Prize before she turned 30.',
    claimToFame: 'Owns product and the threat model at once',
    experience: [
      { org: 'Check Point Software', role: 'CTO (prev. Chief Product Officer)', years: '1995–' },
      { org: 'Unit 8200, IDF', role: 'Officer', years: '1988–1993', note: 'Israel Defense Prize' },
    ],
  },

  // ── AI researchers · 2017 ─────────────────────────────────────────────
  'geoffrey-hinton': {
    headline: 'Professor Emeritus, University of Toronto · Turing Award, Nobel Prize',
    location: 'Toronto, Canada',
    about:
      'Kept working on neural networks through the two decades everyone agreed they were a dead end. Turned out to be right. Quit Google in 2023 specifically so he could warn people about what he had built.',
    claimToFame: 'Backpropagation — then the warning',
    experience: [
      { org: 'Google Brain', role: 'VP & Engineering Fellow', years: '2013–2023', note: 'Left to speak freely about AI risk' },
      { org: 'University of Toronto', role: 'Professor', years: '1987–' },
      { org: 'DNNresearch', role: 'Co-founder', years: '2012–2013', note: 'Acquired by Google' },
    ],
  },
  'yann-lecun': {
    headline: 'Chief AI Scientist at Meta · Professor at NYU',
    location: 'New York, USA',
    about:
      'Invented the convolutional network that made computers able to see, back in the late 80s. Now spends roughly equal energy on research and on arguing about it in public.',
    claimToFame: 'Convolutional nets — computer vision starts here',
    experience: [
      { org: 'Meta (FAIR)', role: 'Chief AI Scientist', years: '2013–' },
      { org: 'NYU', role: 'Professor', years: '2003–' },
      { org: 'Bell Labs', role: 'Researcher', years: '1988–1996', note: 'Cheque-reading systems, deployed at scale' },
    ],
  },
  'yoshua-bengio': {
    headline: 'Professor at Université de Montréal · Founder of Mila',
    location: 'Montréal, Canada',
    about:
      'The third of the three Turing Award winners, and the one who stayed academic. Now spends most of his time on AI safety governance rather than capability research.',
    claimToFame: 'Turing Award; the one who worries',
    experience: [
      { org: 'Mila — Quebec AI Institute', role: 'Founder & Scientific Director', years: '1993–' },
      { org: 'Université de Montréal', role: 'Professor', years: '1993–' },
    ],
  },
  'ilya-sutskever': {
    headline: 'Co-founder at Safe Superintelligence · Co-founder at OpenAI',
    location: 'Palo Alto, USA',
    about:
      'Co-authored AlexNet, the paper that started the deep learning era, then co-founded OpenAI and led the research behind GPT. Left in 2024 to start a lab with exactly one product goal.',
    claimToFame: 'AlexNet, then GPT, then walked out',
    experience: [
      { org: 'Safe Superintelligence Inc.', role: 'Co-founder', years: '2024–' },
      { org: 'OpenAI', role: 'Co-founder & Chief Scientist', years: '2015–2024' },
      { org: 'Google Brain', role: 'Research Scientist', years: '2013–2015' },
    ],
  },
  'demis-hassabis': {
    headline: 'Co-founder & CEO at Google DeepMind · Nobel Laureate',
    location: 'London, UK',
    about:
      'Chess master at 13, shipped a hit video game at 17, PhD in neuroscience, then built the lab that solved protein folding. Won a Nobel Prize for it.',
    claimToFame: 'AlphaGo, AlphaFold, and a Nobel',
    experience: [
      { org: 'Google DeepMind', role: 'Co-founder & CEO', years: '2010–', note: 'Acquired by Google for ~$500M in 2014' },
      { org: 'Elixir Studios', role: 'Founder', years: '1998–2005', note: 'Video games' },
    ],
  },
  'andrej-karpathy': {
    headline: 'Founder at Eureka Labs · prev. Director of AI at Tesla, OpenAI',
    location: 'San Francisco, USA',
    about:
      'Ran Autopilot vision at Tesla and was a founding member of OpenAI, but is best known for explaining the whole field better than anyone else — for free, on YouTube.',
    claimToFame: 'Explains it better than the people who invented it',
    experience: [
      { org: 'Eureka Labs', role: 'Founder', years: '2024–' },
      { org: 'OpenAI', role: 'Founding member, then researcher', years: '2015–17, 2023–24' },
      { org: 'Tesla', role: 'Senior Director of AI', years: '2017–2022', note: 'Autopilot vision' },
    ],
  },

  // ── PayPal Mafia · 2002 ───────────────────────────────────────────────
  'peter-thiel': {
    headline: 'Co-founder at Palantir & Founders Fund · Co-founder at PayPal',
    location: 'Miami, USA',
    about:
      'Wrote the first outside cheque into Facebook. Co-founded PayPal and Palantir, and has spent the twenty years since arguing that competition is for losers.',
    claimToFame: 'First $500K into Facebook',
    experience: [
      { org: 'Founders Fund', role: 'Partner', years: '2005–' },
      { org: 'Palantir', role: 'Co-founder & Chairman', years: '2003–' },
      { org: 'PayPal', role: 'Co-founder & CEO', years: '1998–2002' },
    ],
  },
  'elon-musk': {
    headline: 'CEO at SpaceX, Tesla, xAI · Owner of X',
    location: 'Austin, USA',
    about:
      'Runs more companies than most people have jobs. Landed rockets on boats, made electric cars desirable, bought a social network mostly to post on it.',
    claimToFame: 'Reusable rockets. Also the posting.',
    experience: [
      { org: 'SpaceX', role: 'Founder & CEO', years: '2002–' },
      { org: 'Tesla', role: 'CEO', years: '2008–' },
      { org: 'X.com / PayPal', role: 'Co-founder', years: '1999–2000', note: 'Ousted as CEO while on honeymoon' },
    ],
  },
  'reid-hoffman': {
    headline: 'Co-founder at LinkedIn · Partner at Greylock',
    location: 'Palo Alto, USA',
    about:
      'Founded the professional network this card is imitating. Known as the most connected person in Silicon Valley, and the calmest man in every room he is in.',
    claimToFame: 'Founded LinkedIn. Yes, that LinkedIn.',
    experience: [
      { org: 'Greylock Partners', role: 'Partner', years: '2009–' },
      { org: 'LinkedIn', role: 'Co-founder & CEO', years: '2003–2007', note: 'Acquired by Microsoft, $26.2B' },
      { org: 'PayPal', role: 'COO', years: '2000–2002' },
    ],
  },
  'max-levchin': {
    headline: 'Founder & CEO at Affirm · Co-founder at PayPal',
    location: 'San Francisco, USA',
    about:
      'Built the fraud detection system that kept PayPal alive when organised crime was eating it. Named it Igor, after the specific Russian fraudster who kept beating it.',
    claimToFame: 'Stopped the fraud that nearly killed PayPal',
    experience: [
      { org: 'Affirm', role: 'Founder & CEO', years: '2012–' },
      { org: 'Slide', role: 'Founder', years: '2005–2010', note: 'Acquired by Google' },
      { org: 'PayPal', role: 'Co-founder & CTO', years: '1998–2002' },
    ],
  },
  'david-sacks': {
    headline: 'Partner at Craft Ventures · prev. COO at PayPal, CEO at Yammer',
    location: 'San Francisco, USA',
    about:
      'Ran product and operations at PayPal, then built and sold Yammer to Microsoft for $1.2B. Now equally famous for a podcast.',
    claimToFame: 'Yammer → Microsoft, $1.2B',
    experience: [
      { org: 'Craft Ventures', role: 'Co-founder & Partner', years: '2017–' },
      { org: 'Yammer', role: 'Founder & CEO', years: '2008–2014' },
      { org: 'PayPal', role: 'COO', years: '1999–2002' },
    ],
  },
  'keith-rabois': {
    headline: 'Managing Director at Khosla Ventures · prev. COO at Square',
    location: 'Miami, USA',
    about:
      'Executive at PayPal, LinkedIn and Square, and an early investor in most of the rest. Famous for hiring aggressively and for saying the quiet part loudly.',
    claimToFame: 'Operator at PayPal, LinkedIn and Square',
    experience: [
      { org: 'Khosla Ventures', role: 'Managing Director', years: '2013–' },
      { org: 'Square', role: 'COO', years: '2010–2013' },
      { org: 'LinkedIn', role: 'VP Business & Corp Dev', years: '2005–2007' },
    ],
  },
  'roelof-botha': {
    headline: 'Senior Steward at Sequoia Capital · prev. CFO at PayPal',
    location: 'Menlo Park, USA',
    about:
      'Was PayPal\'s CFO at 28, took it public, then joined Sequoia and led the seed rounds into YouTube, Instagram and Square. Runs the whole firm now.',
    claimToFame: 'Seeded YouTube, Instagram, Square',
    experience: [
      { org: 'Sequoia Capital', role: 'Senior Steward', years: '2003–' },
      { org: 'PayPal', role: 'CFO', years: '2001–2003', note: 'Took the company public at 28' },
    ],
  },

  // ── Wiz · 2020s ───────────────────────────────────────────────────────
  // All four met in Unit 8200, built Adallom together, sold it to Microsoft,
  // ran cloud security there, then left to do the whole thing again.
  'assaf-rappaport': {
    headline: 'Co-founder & CEO at Wiz',
    location: 'Tel Aviv, Israel',
    about:
      'Built a cloud security company, sold it to Microsoft, ran cloud security inside Microsoft, then walked out and built a bigger one. Wiz reached $100M in annual recurring revenue in about eighteen months — turned down Google\'s first offer, then agreed to a much larger second one.',
    claimToFame: 'Said no to $23B, then yes to $32B',
    experience: [
      { org: 'Wiz', role: 'Co-founder & CEO', years: '2020–', note: 'Fastest software company to $100M ARR' },
      { org: 'Microsoft', role: 'GM, Cloud Security', years: '2015–2020' },
      { org: 'Adallom', role: 'Co-founder & CEO', years: '2012–2015', note: 'Acquired by Microsoft' },
    ],
  },
  'ami-luttwak': {
    headline: 'Co-founder & CTO at Wiz',
    location: 'Tel Aviv, Israel',
    about:
      'The technical half of a founding team that has now built cloud security twice. Wiz\'s whole pitch — read the customer\'s cloud without installing anything in it — is his architecture.',
    claimToFame: 'Built cloud security twice',
    experience: [
      { org: 'Wiz', role: 'Co-founder & CTO', years: '2020–' },
      { org: 'Microsoft', role: 'CTO, Cloud Security', years: '2015–2020' },
      { org: 'Adallom', role: 'Co-founder & CTO', years: '2012–2015', note: 'Acquired by Microsoft' },
    ],
  },
  'yinon-costica': {
    headline: 'Co-founder & VP Product at Wiz',
    location: 'Tel Aviv, Israel',
    about:
      'Owns what Wiz actually ships. Same founding four as Adallom, same route through Microsoft, and the one who turns the research into something a customer can buy.',
    claimToFame: 'Turns cloud research into a product',
    experience: [
      { org: 'Wiz', role: 'Co-founder & VP Product', years: '2020–' },
      { org: 'Microsoft', role: 'Cloud Security group', years: '2015–2020' },
      { org: 'Adallom', role: 'Co-founder', years: '2012–2015', note: 'Acquired by Microsoft' },
    ],
  },
  'roy-reznik': {
    headline: 'Co-founder & VP R&D at Wiz',
    location: 'Tel Aviv, Israel',
    about:
      'Runs engineering for a company that scaled faster than almost any software business on record. Fourth of the four, twice over: Unit 8200, then Adallom, then Microsoft, then Wiz.',
    claimToFame: 'Scaled the engineering behind Wiz',
    experience: [
      { org: 'Wiz', role: 'Co-founder & VP R&D', years: '2020–' },
      { org: 'Microsoft', role: 'Cloud Security group', years: '2015–2020' },
      { org: 'Adallom', role: 'Co-founder', years: '2012–2015', note: 'Acquired by Microsoft' },
    ],
  },

  // ── monday.com · 2010s–20s ────────────────────────────────────────────
  'roy-mann': {
    headline: 'Co-founder & CEO at monday.com',
    location: 'Tel Aviv, Israel',
    about:
      'Started it as an internal tool, spent years insisting it could be a category, and took it public on Nasdaq. The product is a board — the bet was that a board is enough for absolutely any team.',
    claimToFame: 'Turned a work board into a public company',
    experience: [
      { org: 'monday.com', role: 'Co-founder & CEO', years: '2012–', note: 'Nasdaq IPO, 2021' },
      { org: 'Wix', role: 'Engineering' },
    ],
  },
  'eran-zinman': {
    headline: 'Co-founder & Co-CEO at monday.com',
    location: 'Tel Aviv, Israel',
    about:
      'Built the technical side of monday.com from the start and moved into running it jointly. The rare co-founder pairing that survived the whole way from a two-person tool to a public company.',
    claimToFame: 'Co-founded it, then co-ran it',
    experience: [
      { org: 'monday.com', role: 'Co-founder & Co-CEO', years: '2012–', note: 'Co-CEO since 2023' },
    ],
  },
  'daniel-lereya': {
    headline: 'Chief Product & Technology Officer at monday.com',
    location: 'Tel Aviv, Israel',
    about:
      'Owns product and engineering at once, through the shift from a single work tool into a platform other products get built on top of.',
    claimToFame: 'Turned the tool into a platform',
    experience: [{ org: 'monday.com', role: 'Chief Product & Technology Officer' }],
  },

  // ── Lemonade · 2015–20s ───────────────────────────────────────────────
  'daniel-schreiber': {
    headline: 'Co-founder & CEO at Lemonade',
    location: 'New York, USA',
    about:
      'Went into insurance knowing nothing about insurance, on the theory that this was the advantage. Lemonade gives away the underwriting profit it doesn\'t use, which turns the industry\'s worst incentive inside out.',
    claimToFame: 'Made insurance a brand people like',
    experience: [
      { org: 'Lemonade', role: 'Co-founder & CEO', years: '2015–', note: 'NYSE IPO, 2020' },
      { org: 'Powermat Technologies', role: 'President' },
    ],
  },
  'shai-wininger': {
    headline: 'Co-founder & President at Lemonade · Co-founder at Fiverr',
    location: 'Tel Aviv, Israel',
    about:
      'Co-founded two companies that both went public, in two industries with nothing in common, and built the product himself both times.',
    claimToFame: 'Founded Fiverr and Lemonade',
    experience: [
      { org: 'Lemonade', role: 'Co-founder & President', years: '2015–' },
      { org: 'Fiverr', role: 'Co-founder', years: '2010–2015' },
    ],
  },
  'ty-sagalow': {
    headline: 'Co-founder & Chief Insurance Officer at Lemonade · 25 years at AIG',
    location: 'New York, USA',
    about:
      'The one person in the building who had actually done insurance — a quarter-century of it at AIG — brought in to write the policy the rest of the company wanted to sell.',
    claimToFame: 'Wrote the policy nobody else could',
    experience: [
      { org: 'Lemonade', role: 'Co-founder & Chief Insurance Officer', years: '2015–' },
      { org: 'AIG', role: 'Senior insurance roles', note: 'Around 25 years' },
    ],
  },

  // ── Mobileye · 2000s–10s ──────────────────────────────────────────────
  'amnon-shashua': {
    headline: 'CEO at Mobileye · Professor at the Hebrew University of Jerusalem',
    location: 'Jerusalem, Israel',
    about:
      'Argued that a single camera and enough computer vision could do what everyone else needed lidar for, and was right. Mobileye sold to Intel for over $15B; he has co-founded two more companies since, in assistive devices and in language models.',
    claimToFame: 'One camera, then a $15B exit',
    experience: [
      { org: 'Mobileye', role: 'Co-founder, CTO, now CEO', years: '1999–', note: 'Acquired by Intel for $15.3B in 2017' },
      { org: 'AI21 Labs', role: 'Co-founder', years: '2017–' },
      { org: 'OrCam', role: 'Co-founder', years: '2010–' },
      { org: 'Hebrew University of Jerusalem', role: 'Professor of Computer Science' },
    ],
  },
  'ziv-aviram': {
    headline: 'Co-founder at Mobileye & OrCam',
    location: 'Jerusalem, Israel',
    about:
      'Ran Mobileye as CEO for its entire independent life — through the IPO and the Intel acquisition — while his co-founder handled the vision research. Started a second company with the same partner along the way.',
    claimToFame: 'Ran Mobileye from founding to Intel',
    experience: [
      { org: 'Mobileye', role: 'Co-founder & CEO', years: '1999–2018', note: 'IPO 2014, acquired by Intel 2017' },
      { org: 'OrCam', role: 'Co-founder & CEO', years: '2010–' },
    ],
  },
  'gaby-hayon': {
    headline: 'EVP Research & Development at Mobileye',
    location: 'Jerusalem, Israel',
    about:
      'Has run the research and development organisation at Mobileye for around two decades — the same problem, the same building, across an IPO and an acquisition.',
    claimToFame: 'Two decades of the same hard problem',
    experience: [{ org: 'Mobileye', role: 'EVP Research & Development' }],
  },

  // ── Fiverr · 2010s ────────────────────────────────────────────────────
  'micha-kaufman': {
    headline: 'Co-founder & CEO at Fiverr',
    location: 'Tel Aviv, Israel',
    about:
      'Built a marketplace on the premise that a service could be listed and bought like a product, starting at five dollars. Took it public on the NYSE.',
    claimToFame: 'Made freelance work a shelf item',
    experience: [
      { org: 'Fiverr', role: 'Co-founder & CEO', years: '2010–', note: 'NYSE IPO, 2019' },
    ],
  },
  'gali-arnon': {
    headline: 'Chief Marketing Officer at Fiverr',
    location: 'Tel Aviv, Israel',
    about:
      'Ran the brand work that made a freelance marketplace feel like a movement — "In Doers We Trust" is hers.',
    claimToFame: '"In Doers We Trust"',
    experience: [{ org: 'Fiverr', role: 'Chief Marketing Officer' }],
  },

  // ── Israeli cyber ─────────────────────────────────────────────────────
  'tomer-weingarten': {
    headline: 'Co-founder & CEO at SentinelOne',
    location: 'Tel Aviv, Israel',
    about:
      'Bet that endpoint security had to run itself — detect, decide and roll back the damage without a human in the loop — at a time when the industry still sold dashboards for analysts to stare at. Took it public on the NYSE.',
    claimToFame: 'Security that fights back on its own',
    experience: [{ org: 'SentinelOne', role: 'Co-founder & CEO', years: '2013–', note: 'NYSE IPO, 2021' }],
  },
  'udi-mokady': {
    headline: 'Founder & Executive Chairman at CyberArk',
    location: 'Tel Aviv, Israel',
    about:
      'Built a company around one narrow idea — the accounts with god-mode access are the ones attackers actually want — and stayed on it for twenty-five years while the rest of the industry chased perimeters.',
    claimToFame: 'Owns the keys to the kingdom',
    experience: [
      { org: 'CyberArk', role: 'Founder, CEO, now Executive Chairman', years: '1999–', note: 'Nasdaq IPO, 2014' },
    ],
  },
  'mickey-boodaei': {
    headline: 'Co-founder & CEO at Transmit Security · Co-founder at Imperva & Trusteer',
    location: 'Tel Aviv, Israel',
    about:
      'Three security companies, three exits or unicorns. Trusteer went to IBM, Imperva went public, and he started again both times.',
    claimToFame: 'Founded three, sold two',
    experience: [
      { org: 'Transmit Security', role: 'Co-founder & CEO', years: '2014–' },
      { org: 'Trusteer', role: 'Co-founder & CEO', years: '2006–2013', note: 'Acquired by IBM' },
      { org: 'Imperva', role: 'Co-founder', years: '2002–2006' },
    ],
  },

  // ── OpenAI ────────────────────────────────────────────────────────────
  'sam-altman': {
    headline: 'Co-founder & CEO at OpenAI · prev. President of Y Combinator',
    location: 'San Francisco, USA',
    about:
      'Ran the startup accelerator that funded a generation of companies, then took over a research lab and turned it into the fastest-growing product in history. The board fired him in November 2023; he was back within a week.',
    claimToFame: 'Fired on a Friday, back by Wednesday',
    experience: [
      { org: 'OpenAI', role: 'Co-founder & CEO', years: '2015–' },
      { org: 'Y Combinator', role: 'President', years: '2014–2019' },
      { org: 'Loopt', role: 'Co-founder & CEO', years: '2005–2012', note: "In Y Combinator's first batch" },
    ],
  },
  'greg-brockman': {
    headline: 'Co-founder & President at OpenAI · prev. CTO at Stripe',
    location: 'San Francisco, USA',
    about:
      'Was Stripe\'s CTO before he was thirty, left to help start OpenAI, and is famous internally for simply out-working everyone in the building. Walked out with Altman during the board crisis and came back with him.',
    claimToFame: 'Stripe CTO, then OpenAI from day one',
    experience: [
      { org: 'OpenAI', role: 'Co-founder & President', years: '2015–' },
      { org: 'Stripe', role: 'CTO', years: '2010–2015', note: 'Employee number four' },
    ],
  },
  'mira-murati': {
    headline: 'Founder & CEO at Thinking Machines Lab · prev. CTO at OpenAI',
    location: 'San Francisco, USA',
    about:
      'Ran the research and product organisation that shipped ChatGPT and DALL·E, and was named interim CEO in the middle of the five-day crisis that nearly ended the company. Left in 2024 to start her own lab.',
    claimToFame: 'Interim CEO during the OpenAI coup',
    experience: [
      { org: 'Thinking Machines Lab', role: 'Founder & CEO', years: '2025–' },
      { org: 'OpenAI', role: 'CTO', years: '2018–2024', note: 'Interim CEO, November 2023' },
      { org: 'Tesla', role: 'Product manager, Model X', years: '2013–2016' },
    ],
  },
  'jakub-pachocki': {
    headline: 'Chief Scientist at OpenAI',
    location: 'San Francisco, USA',
    about:
      'A competitive-programming champion turned researcher who led the reasoning work behind OpenAI\'s frontier models, and took over as Chief Scientist when Ilya Sutskever left.',
    claimToFame: 'Took the Chief Scientist chair after Ilya',
    experience: [{ org: 'OpenAI', role: 'Researcher, now Chief Scientist', years: '2017–' }],
  },
  'brad-lightcap': {
    headline: 'Chief Operating Officer at OpenAI',
    location: 'San Francisco, USA',
    about:
      'The one turning a research lab into a business — pricing, enterprise deals, and the commercial structure underneath a company that started out as a non-profit.',
    claimToFame: 'Made the research lab a business',
    experience: [
      { org: 'OpenAI', role: 'COO', years: '2018–' },
      { org: 'Y Combinator', role: 'YC Continuity fund' },
    ],
  },
  'wojciech-zaremba': {
    headline: 'Co-founder at OpenAI',
    location: 'San Francisco, USA',
    about:
      'One of the original founding researchers. Led the robotics team that taught a hand to solve a Rubik\'s cube, then moved onto the work that became code generation.',
    claimToFame: 'Robot hand, Rubik\'s cube',
    experience: [{ org: 'OpenAI', role: 'Co-founder & researcher', years: '2015–' }],
  },

  // ── DeepMind ──────────────────────────────────────────────────────────
  'shane-legg': {
    headline: 'Co-founder & Chief AGI Scientist at Google DeepMind',
    location: 'London, UK',
    about:
      'Was using the term "artificial general intelligence" in his PhD work when saying it out loud still got you laughed at, and co-founded a company on the premise that it was actually achievable.',
    claimToFame: 'Put the "AGI" in everyone\'s vocabulary',
    experience: [{ org: 'Google DeepMind', role: 'Co-founder & Chief AGI Scientist', years: '2010–' }],
  },
  'david-silver': {
    headline: 'Principal Research Scientist at Google DeepMind · Professor at UCL',
    location: 'London, UK',
    about:
      'Led AlphaGo, then AlphaZero — the system that taught itself Go, chess and shogi from nothing but the rules and beat everything humans had built.',
    claimToFame: 'AlphaGo, then AlphaZero',
    experience: [
      { org: 'Google DeepMind', role: 'Principal Research Scientist', years: '2013–' },
      { org: 'University College London', role: 'Professor' },
    ],
  },
  'oriol-vinyals': {
    headline: 'VP of Research at Google DeepMind',
    location: 'London, UK',
    about:
      'Co-authored the sequence-to-sequence paper that reframed translation as a learning problem, led the StarCraft II agent that beat professionals, and now co-leads Gemini.',
    claimToFame: 'Seq2seq, StarCraft, now Gemini',
    experience: [
      { org: 'Google DeepMind', role: 'VP of Research, co-lead on Gemini', years: '2016–' },
      { org: 'Google Brain', role: 'Research Scientist', years: '2013–2016' },
    ],
  },
  'mustafa-suleyman': {
    headline: 'CEO at Microsoft AI · Co-founder at DeepMind',
    location: 'London, UK',
    about:
      'Co-founded DeepMind at 26 without a technical background, ran its applied and ethics work, then founded Inflection — and took the whole team to Microsoft in one of the strangest deals the industry has seen.',
    claimToFame: 'Founded DeepMind, then Inflection, now runs Microsoft AI',
    experience: [
      { org: 'Microsoft AI', role: 'CEO', years: '2024–' },
      { org: 'Inflection AI', role: 'Co-founder & CEO', years: '2022–2024' },
      { org: 'DeepMind', role: 'Co-founder', years: '2010–2019' },
    ],
  },

  // ── Other AI labs ─────────────────────────────────────────────────────
  'dario-amodei': {
    headline: 'Co-founder & CEO at Anthropic · prev. VP of Research at OpenAI',
    location: 'San Francisco, USA',
    about:
      'Ran research at OpenAI, left with a group of colleagues over how the work should be done, and started a lab built around the argument that you can push capabilities and take the risk seriously at the same time.',
    claimToFame: 'Walked out of OpenAI and built Anthropic',
    experience: [
      { org: 'Anthropic', role: 'Co-founder & CEO', years: '2021–' },
      { org: 'OpenAI', role: 'VP of Research', years: '2016–2020' },
      { org: 'Google Brain', role: 'Research Scientist' },
    ],
  },
  'aravind-srinivas': {
    headline: 'Co-founder & CEO at Perplexity · prev. OpenAI',
    location: 'San Francisco, USA',
    about:
      'Left research to attack the one product nobody had managed to dent in twenty years, on the bet that people want an answer rather than ten blue links.',
    claimToFame: 'Went straight at Google Search',
    experience: [
      { org: 'Perplexity AI', role: 'Co-founder & CEO', years: '2022–' },
      { org: 'OpenAI', role: 'Research Scientist' },
    ],
  },
  'alexandr-wang': {
    headline: 'Chief AI Officer at Meta · Founder at Scale AI',
    location: 'San Francisco, USA',
    about:
      'Dropped out of MIT at nineteen to build the company that labelled the data everyone else\'s models trained on, from self-driving to defence. Meta paid billions for a stake in it and brought him in to run its superintelligence effort.',
    claimToFame: 'Labelled the data the whole industry trained on',
    experience: [
      { org: 'Meta', role: 'Chief AI Officer', years: '2025–' },
      { org: 'Scale AI', role: 'Founder & CEO', years: '2016–2025' },
    ],
  },
  'emad-mostaque': {
    headline: 'Founder at Stability AI · prev. hedge fund analyst',
    location: 'London, UK',
    about:
      'Funded and released Stable Diffusion into the open, which put image generation on ordinary laptops overnight and set off most of the arguments the field is still having. Resigned in 2024 amid questions about the company\'s finances and his own account of his past.',
    claimToFame: 'Open-sourced image generation',
    experience: [{ org: 'Stability AI', role: 'Founder & CEO', years: '2019–2024' }],
  },

  // ── SaaS & enterprise ─────────────────────────────────────────────────
  'patrick-collison': {
    headline: 'Co-founder & CEO at Stripe',
    location: 'San Francisco, USA',
    about:
      'Won Ireland\'s national science prize as a teenager, sold his first company at nineteen, then built the payments company on the premise that accepting money online should be seven lines of code rather than a three-month bank negotiation.',
    claimToFame: 'Seven lines of code to take money',
    experience: [
      { org: 'Stripe', role: 'Co-founder & CEO', years: '2010–' },
      { org: 'Auctomatic', role: 'Co-founder', years: '2007–2008', note: 'Acquired at 19' },
    ],
  },
  'drew-houston': {
    headline: 'Co-founder & CEO at Dropbox',
    location: 'San Francisco, USA',
    about:
      'Built it because he kept forgetting his USB stick. The demo video did more for the launch than the product did, and the folder-that-just-syncs became the way a generation understood the cloud.',
    claimToFame: 'A folder that just syncs',
    experience: [{ org: 'Dropbox', role: 'Co-founder & CEO', years: '2007–', note: 'Nasdaq IPO, 2018' }],
  },
  'aaron-levie': {
    headline: 'Co-founder & CEO at Box',
    location: 'Redwood City, USA',
    about:
      'Started it in a college dorm, dropped out to run it, and has spent twenty years selling storage and workflow to enterprises while being funnier online than the job requires.',
    claimToFame: 'Enterprise software, with jokes',
    experience: [{ org: 'Box', role: 'Co-founder & CEO', years: '2005–', note: 'NYSE IPO, 2015' }],
  },
  'tobi-lutke': {
    headline: 'Founder & CEO at Shopify',
    location: 'Ottawa, Canada',
    about:
      'Tried to sell snowboards online, hated every piece of software available, and built his own. A core Ruby on Rails contributor who ended up running one of the largest commerce platforms in the world.',
    claimToFame: 'Built a store, accidentally built the platform',
    experience: [{ org: 'Shopify', role: 'Founder & CEO', years: '2006–' }],
  },
  'melanie-perkins': {
    headline: 'Co-founder & CEO at Canva',
    location: 'Sydney, Australia',
    about:
      'Was rejected by more than a hundred investors before anyone funded the idea that design tools should be usable by people who are not designers. Started with school yearbooks and worked outwards.',
    claimToFame: 'Design tools for the other 99%',
    experience: [
      { org: 'Canva', role: 'Co-founder & CEO', years: '2013–' },
      { org: 'Fusion Books', role: 'Co-founder', note: 'Online school yearbooks' },
    ],
  },
  'stewart-butterfield': {
    headline: 'Co-founder at Slack · Co-founder at Flickr',
    location: 'Vancouver, Canada',
    about:
      'Has now failed at building an online game twice, and salvaged a billion-dollar company from the wreckage both times: Flickr came out of one, Slack came out of the other.',
    claimToFame: 'Two failed games, two huge exits',
    experience: [
      { org: 'Slack', role: 'Co-founder & CEO', years: '2013–2022', note: 'Acquired by Salesforce, $27.7B' },
      { org: 'Flickr', role: 'Co-founder', years: '2004–2008', note: 'Acquired by Yahoo' },
    ],
  },
  'frank-slootman': {
    headline: 'prev. CEO at Snowflake, ServiceNow and Data Domain',
    location: 'Montana, USA',
    about:
      'Took three companies public and is known less for founding things than for arriving and raising the tempo — his own book on the subject is called Amp It Up. Snowflake\'s listing was the largest software IPO ever.',
    claimToFame: 'Three IPOs, none of them his own company',
    experience: [
      { org: 'Snowflake', role: 'CEO', years: '2019–2024', note: 'Largest software IPO on record' },
      { org: 'ServiceNow', role: 'CEO', years: '2011–2017' },
      { org: 'Data Domain', role: 'CEO', note: 'Acquired by EMC' },
    ],
  },
  'eric-yuan': {
    headline: 'Founder & CEO at Zoom · prev. VP Engineering at Cisco WebEx',
    location: 'San Jose, USA',
    about:
      'Was denied a US visa eight times before finally getting in. Spent years building video conferencing at WebEx, got tired of customers being unhappy with it, and left to build the version he wanted.',
    claimToFame: 'Rejected eight times, then built Zoom',
    experience: [
      { org: 'Zoom', role: 'Founder & CEO', years: '2011–', note: 'Nasdaq IPO, 2019' },
      { org: 'Cisco WebEx', role: 'VP of Engineering', years: '1997–2011' },
    ],
  },
  'marc-benioff': {
    headline: 'Founder & CEO at Salesforce',
    location: 'San Francisco, USA',
    about:
      'Spent thirteen years at Oracle, then launched a company whose entire early marketing was a red circle with a line through the word "software". Software as a service was a heresy at the time; it is now the default.',
    claimToFame: 'Killed software by selling it as a service',
    experience: [
      { org: 'Salesforce', role: 'Founder & CEO', years: '1999–' },
      { org: 'Oracle', role: 'Various, up to VP', years: '1986–1999' },
    ],
  },
  'larry-ellison': {
    headline: 'Founder & CTO at Oracle',
    location: 'Austin, USA',
    about:
      'Built the first commercial SQL database and then out-competed everyone in enterprise sales for four decades. Won the America\'s Cup, bought most of a Hawaiian island, and never got quieter about any of it.',
    claimToFame: 'Won the database war and the regatta',
    experience: [
      { org: 'Oracle', role: 'Co-founder, CEO, now CTO & Chairman', years: '1977–' },
    ],
  },
  'parker-conrad': {
    headline: 'Founder & CEO at Rippling · prev. founder at Zenefits',
    location: 'San Francisco, USA',
    about:
      'Was forced out of the company he founded after a compliance scandal, then started a directly competing one and built it into something much bigger. Argues loudly that "do one thing well" is bad advice for business software.',
    claimToFame: 'Fired from his own company, did it again',
    experience: [
      { org: 'Rippling', role: 'Founder & CEO', years: '2016–' },
      { org: 'Zenefits', role: 'Co-founder & CEO', years: '2013–2016' },
    ],
  },

  // ── Developer tools & infrastructure ──────────────────────────────────
  'dylan-field': {
    headline: 'Co-founder & CEO at Figma',
    location: 'San Francisco, USA',
    about:
      'Took a Thiel Fellowship to drop out and spent four years building a design tool in the browser before shipping anything. Adobe agreed to buy it for $20B; regulators killed the deal; he took it public instead.',
    claimToFame: 'Design, multiplayer, in the browser',
    experience: [
      { org: 'Figma', role: 'Co-founder & CEO', years: '2012–', note: 'Adobe deal blocked in 2023; IPO in 2025' },
    ],
  },
  'ivan-zhao': {
    headline: 'Co-founder & CEO at Notion',
    location: 'San Francisco, USA',
    about:
      'Nearly ran out of money twice pursuing the idea that software should be built out of blocks users assemble themselves, rather than apps someone else finished for them.',
    claimToFame: 'Lego bricks instead of apps',
    experience: [{ org: 'Notion', role: 'Co-founder & CEO', years: '2013–' }],
  },
  'vlad-magdalin': {
    headline: 'Co-founder at Webflow',
    location: 'San Francisco, USA',
    about:
      'Spent roughly a decade and several failed attempts on the same idea — that designers should be able to build real websites without writing the code — before it finally worked.',
    claimToFame: 'Ten years to make no-code work',
    experience: [{ org: 'Webflow', role: 'Co-founder & CEO', years: '2013–' }],
  },
  'guillermo-rauch': {
    headline: 'Founder & CEO at Vercel · creator of Socket.IO',
    location: 'San Francisco, USA',
    about:
      'Wrote Socket.IO as a teenager in Argentina, then built the company behind Next.js and a deployment model where every push to a branch gets its own live URL.',
    claimToFame: 'Next.js and a URL for every push',
    experience: [{ org: 'Vercel', role: 'Founder & CEO', years: '2015–' }],
  },
  'mitchell-hashimoto': {
    headline: 'Co-founder at HashiCorp · creator of Vagrant, Terraform and Ghostty',
    location: 'Los Angeles, USA',
    about:
      'Wrote Vagrant as a side project, then Terraform, and turned the collection into an infrastructure company that went public. Stepped back from the business to go and write code again.',
    claimToFame: 'Terraform — infrastructure as code',
    experience: [
      { org: 'HashiCorp', role: 'Co-founder', years: '2012–2023', note: 'Nasdaq IPO, 2021' },
    ],
  },
  'sid-sijbrandij': {
    headline: 'Co-founder at GitLab',
    location: 'San Francisco, USA',
    about:
      'Runs a company where the entire operating manual — strategy, salaries, how decisions get made — is published on the open web, and where nobody has ever had an office.',
    claimToFame: 'Published the whole company handbook',
    experience: [{ org: 'GitLab', role: 'Co-founder & CEO', years: '2014–', note: 'Nasdaq IPO, 2021' }],
  },

  // ── Marketplaces & commerce ───────────────────────────────────────────
  'jack-ma': {
    headline: 'Founder at Alibaba · former English teacher',
    location: 'Hangzhou, China',
    about:
      'Taught English in Hangzhou, failed to get hired at KFC, and built the company that put Chinese manufacturing in front of the world. Largely withdrew from public life after a 2020 speech criticising financial regulators.',
    claimToFame: 'Teacher to the largest IPO in history',
    experience: [{ org: 'Alibaba Group', role: 'Founder & Executive Chairman', years: '1999–2019' }],
  },
  'pierre-omidyar': {
    headline: 'Founder at eBay · Founder at Omidyar Network',
    location: 'Honolulu, USA',
    about:
      'Wrote an auction site over a long weekend to see whether strangers would trade with each other honestly. They would. Has spent most of the time since giving the proceeds away.',
    claimToFame: 'Proved strangers will trade online',
    experience: [
      { org: 'eBay', role: 'Founder & Chairman', years: '1995–' },
      { org: 'Omidyar Network', role: 'Founder', years: '2004–' },
    ],
  },
  'logan-green': {
    headline: 'Co-founder at Lyft',
    location: 'San Francisco, USA',
    about:
      'Started with intercity carpooling for students, turned it into the friendlier of the two ride-hailing companies, and got it public in the same year as the other one.',
    claimToFame: 'Carpooling, then the pink moustache',
    experience: [
      { org: 'Lyft', role: 'Co-founder & CEO', years: '2012–2023', note: 'Nasdaq IPO, 2019' },
      { org: 'Zimride', role: 'Co-founder', years: '2007–2013' },
    ],
  },
  'tony-xu': {
    headline: 'Co-founder & CEO at DoorDash',
    location: 'San Francisco, USA',
    about:
      'Started by delivering the orders himself out of a business-school project, and won the category by going after suburbs everyone else considered too thin to be worth the driver.',
    claimToFame: 'Won delivery by starting in the suburbs',
    experience: [{ org: 'DoorDash', role: 'Co-founder & CEO', years: '2013–', note: 'NYSE IPO, 2020' }],
  },

  // ── Consumer & creator ────────────────────────────────────────────────
  'kevin-systrom': {
    headline: 'Co-founder at Instagram',
    location: 'San Francisco, USA',
    about:
      'Stripped a struggling check-in app down to photos, filters and a feed. Sold to Facebook for a billion dollars with thirteen employees and no revenue, then spent six years running it inside.',
    claimToFame: '$1B, 13 employees, no revenue',
    experience: [
      { org: 'Instagram', role: 'Co-founder & CEO', years: '2010–2018', note: 'Acquired by Facebook, $1B' },
      { org: 'Google', role: 'Product marketing' },
    ],
  },
  'evan-spiegel': {
    headline: 'Co-founder & CEO at Snap',
    location: 'Los Angeles, USA',
    about:
      'Built the app around the idea that a message should disappear, and turned down three billion dollars from Facebook at 23 to keep it. Camera-first, and stubbornly its own thing ever since.',
    claimToFame: 'Turned down $3B at 23',
    experience: [{ org: 'Snap Inc.', role: 'Co-founder & CEO', years: '2011–', note: 'NYSE IPO, 2017' }],
  },
  'ben-silbermann': {
    headline: 'Co-founder at Pinterest',
    location: 'San Francisco, USA',
    about:
      'Left a job in Google\'s ad department to build a place for collecting things you like. Grew quietly for years, mostly outside the tech press, into one of the largest consumer products on the internet.',
    claimToFame: 'Compounded quietly for a decade',
    experience: [
      { org: 'Pinterest', role: 'Co-founder & CEO', years: '2010–2022', note: 'NYSE IPO, 2019' },
      { org: 'Google', role: 'Online sales & operations' },
    ],
  },
  'daniel-ek': {
    headline: 'Founder & CEO at Spotify',
    location: 'Stockholm, Sweden',
    about:
      'Spent two years negotiating with record labels that had spent a decade suing their own customers, and came out with a product that made streaming feel faster than the piracy it replaced.',
    claimToFame: 'Licensed the whole catalogue',
    experience: [
      { org: 'Spotify', role: 'Founder & CEO', years: '2006–', note: 'NYSE direct listing, 2018' },
      { org: 'µTorrent', role: 'CEO' },
    ],
  },

  // ── Apple ─────────────────────────────────────────────────────────────
  'steve-jobs': {
    headline: 'Co-founder & CEO at Apple · Founder at NeXT · CEO at Pixar',
    location: 'Palo Alto, USA',
    about:
      'Co-founded Apple, was pushed out of it in 1985, spent twelve years building NeXT and Pixar, and came back to run the most valuable turnaround in corporate history. The iPod, iPhone and iPad all shipped in that second act.',
    claimToFame: 'Fired from Apple, came back and saved it',
    experience: [
      { org: 'Apple', role: 'Co-founder; CEO', years: '1976–1985, 1997–2011' },
      { org: 'Pixar', role: 'CEO', years: '1986–2006', note: 'Acquired by Disney' },
      { org: 'NeXT', role: 'Founder & CEO', years: '1985–1997', note: 'Acquired by Apple; its OS became macOS' },
    ],
  },
  'jony-ive': {
    headline: 'Founder at LoveFrom · prev. Chief Design Officer at Apple',
    location: 'San Francisco, USA',
    about:
      'Designed the iMac, iPod, iPhone and the aluminium unibody that everyone else then copied. Left in 2019 to start his own studio; now working on hardware with OpenAI.',
    claimToFame: 'Drew the iMac, iPod and iPhone',
    experience: [
      { org: 'LoveFrom', role: 'Founder', years: '2019–' },
      { org: 'Apple', role: 'Chief Design Officer', years: '1992–2019' },
    ],
  },
  'tim-cook': {
    headline: 'CEO at Apple',
    location: 'Cupertino, USA',
    about:
      'Arrived from Compaq to fix a supply chain that was strangling the company, shut down the factories, and turned operations into a weapon. Has run Apple since 2011 and taken it past three trillion dollars.',
    claimToFame: 'Turned the supply chain into the moat',
    experience: [
      { org: 'Apple', role: 'SVP Operations, COO, now CEO', years: '1998–' },
      { org: 'Compaq', role: 'VP of Corporate Materials' },
      { org: 'IBM', role: 'Director of North American fulfilment', years: '1982–1994' },
    ],
  },
  'scott-forstall': {
    headline: 'prev. SVP of iOS Software at Apple',
    location: 'Palo Alto, USA',
    about:
      'Came to Apple through the NeXT acquisition and ran the software side of the iPhone from the beginning. Left in 2012 after the Maps launch; has since produced on Broadway.',
    claimToFame: 'Ran iPhone software from day one',
    experience: [
      { org: 'Apple', role: 'SVP, iOS Software', years: '1997–2012' },
      { org: 'NeXT', role: 'Engineer' },
    ],
  },
  'phil-schiller': {
    headline: 'Apple Fellow · prev. SVP Worldwide Marketing at Apple',
    location: 'Cupertino, USA',
    about:
      'Ran Apple marketing across the entire Jobs second act and was the reliable second voice on stage at every keynote. Now runs the App Store as an Apple Fellow.',
    claimToFame: 'Every keynote, for twenty years',
    experience: [{ org: 'Apple', role: 'SVP Worldwide Marketing, now Apple Fellow', years: '1997–' }],
  },
  'tony-fadell': {
    headline: 'Founder at Nest · prev. SVP of the iPod division at Apple',
    location: 'Paris, France',
    about:
      'Pitched the iPod, built the team that shipped it, and worked on the first iPhone. Left to build a thermostat nobody thought was interesting until Google paid $3.2B for it.',
    claimToFame: 'Built the iPod, then sold Nest to Google',
    experience: [
      { org: 'Nest Labs', role: 'Founder & CEO', years: '2010–2016', note: 'Acquired by Google, $3.2B' },
      { org: 'Apple', role: 'SVP, iPod Division', years: '2001–2008' },
      { org: 'General Magic', role: 'Engineer' },
    ],
  },
  'eddy-cue': {
    headline: 'SVP of Services at Apple',
    location: 'Cupertino, USA',
    about:
      'Built the iTunes Store and did the negotiating that got the record labels to sell singles for 99 cents — the deal that made the iPod more than a nice piece of hardware.',
    claimToFame: 'Got the labels to sell 99-cent songs',
    experience: [{ org: 'Apple', role: 'SVP, Services', years: '1989–' }],
  },
  'bertrand-serlet': {
    headline: 'prev. SVP of Mac Software Engineering at Apple',
    location: 'San Francisco, USA',
    about:
      'Came from Xerox PARC via NeXT and ran the software engineering that turned NeXTSTEP into Mac OS X — the operating system underneath every Apple product since.',
    claimToFame: 'Turned NeXTSTEP into Mac OS X',
    experience: [
      { org: 'Apple', role: 'SVP, Software Engineering', years: '1997–2011' },
      { org: 'NeXT', role: 'Engineer' },
      { org: 'Xerox PARC', role: 'Researcher' },
    ],
  },

  // ── Facebook / Meta ───────────────────────────────────────────────────
  'mark-zuckerberg': {
    headline: 'Founder & CEO at Meta',
    location: 'Palo Alto, USA',
    about:
      'Started it in a Harvard dorm at nineteen and has never given up control. Bought Instagram and WhatsApp before either was obviously essential, and bet the company\'s name on virtual reality.',
    claimToFame: 'Bought Instagram and WhatsApp early',
    experience: [
      { org: 'Meta (Facebook)', role: 'Founder & CEO', years: '2004–', note: 'Nasdaq IPO 2012; renamed Meta 2021' },
    ],
  },
  'sheryl-sandberg': {
    headline: 'prev. COO at Meta · prev. VP Global Online Sales at Google',
    location: 'Menlo Park, USA',
    about:
      'Built the advertising machine that turned Facebook from a large website into one of the most profitable businesses ever, having done a version of the same job at Google first.',
    claimToFame: 'Built the ad business at Google, then Facebook',
    experience: [
      { org: 'Meta', role: 'COO', years: '2008–2022' },
      { org: 'Google', role: 'VP, Global Online Sales & Operations', years: '2001–2008' },
      { org: 'US Department of the Treasury', role: 'Chief of Staff' },
    ],
  },
  'andrew-bosworth': {
    headline: 'CTO at Meta',
    location: 'Seattle, USA',
    about:
      'Built the original News Feed, then ads, and now runs Reality Labs — the hardware bet. Known internally for arguing his position in public memos rather than in private.',
    claimToFame: 'Wrote News Feed, now runs the headsets',
    experience: [{ org: 'Meta', role: 'Engineer, now CTO', years: '2006–' }],
  },
  'chris-cox': {
    headline: 'Chief Product Officer at Meta',
    location: 'San Francisco, USA',
    about:
      'Joined as an engineer in 2005, wrote much of the early News Feed, and has been the person most associated with what the product is actually for. Left in 2019 and came back a year later.',
    claimToFame: 'The product conscience of Facebook',
    experience: [{ org: 'Meta', role: 'Engineer, now Chief Product Officer', years: '2005–' }],
  },
  'mike-schroepfer': {
    headline: 'prev. CTO at Meta · prev. VP Engineering at Mozilla',
    location: 'Menlo Park, USA',
    about:
      'Ran the engineering behind scaling Facebook to billions of users, and later the AI research organisation. Stepped down as CTO in 2022 to work on climate.',
    claimToFame: 'Scaled it to a third of the planet',
    experience: [
      { org: 'Meta', role: 'VP Engineering, then CTO', years: '2008–2022' },
      { org: 'Mozilla', role: 'VP of Engineering' },
    ],
  },
  'dustin-moskovitz': {
    headline: 'Co-founder & CEO at Asana · Co-founder at Facebook',
    location: 'San Francisco, USA',
    about:
      'Zuckerberg\'s roommate and Facebook\'s first CTO. Left in 2008 to build the internal task tool he wished they\'d had, and has given away most of the proceeds since.',
    claimToFame: 'Left Facebook to build the to-do list',
    experience: [
      { org: 'Asana', role: 'Co-founder & CEO', years: '2008–', note: 'NYSE direct listing, 2020' },
      { org: 'Facebook', role: 'Co-founder & CTO', years: '2004–2008' },
    ],
  },
  'naomi-gleit': {
    headline: 'Head of Product at Meta',
    location: 'Menlo Park, USA',
    about:
      'Employee number twenty-nine and, after Zuckerberg, the longest-serving person in the building. Built the growth team whose methods the rest of the industry then copied wholesale.',
    claimToFame: 'Wrote the growth playbook everyone copied',
    experience: [{ org: 'Meta', role: 'Growth, now Head of Product', years: '2005–' }],
  },
  'javier-olivan': {
    headline: 'Chief Operating Officer at Meta',
    location: 'Menlo Park, USA',
    about:
      'Hired in 2007 to make Facebook work outside the United States, built the growth organisation that took it from a mostly American site to a global one, and now runs operations.',
    claimToFame: 'Took Facebook international',
    experience: [{ org: 'Meta', role: 'Growth, now COO', years: '2007–' }],
  },

  // ── Google ────────────────────────────────────────────────────────────
  'larry-page': {
    headline: 'Co-founder at Google · prev. CEO at Alphabet',
    location: 'Palo Alto, USA',
    about:
      'Wrote PageRank as a Stanford graduate student, co-founded the company on it, and restructured the whole thing into Alphabet so the odd bets could be funded separately. Has been almost entirely absent from public life since 2019.',
    claimToFame: 'PageRank, then Alphabet',
    experience: [
      { org: 'Alphabet', role: 'Co-founder & CEO', years: '2015–2019' },
      { org: 'Google', role: 'Co-founder & CEO', years: '1998–2001, 2011–2015' },
    ],
  },
  'sergey-brin': {
    headline: 'Co-founder at Google',
    location: 'Palo Alto, USA',
    about:
      'The other half of the Stanford pair. Ran the moonshot lab that produced self-driving cars and Glass, then came back to the office in 2023 to work on Gemini directly.',
    claimToFame: 'Co-founded Google, then ran the moonshots',
    experience: [
      { org: 'Google / Alphabet', role: 'Co-founder; President of Alphabet', years: '1998–2019' },
      { org: 'Google X', role: 'Lead' },
    ],
  },
  'eric-schmidt': {
    headline: 'prev. CEO at Google · prev. CEO at Novell',
    location: 'Palo Alto, USA',
    about:
      'Brought in as the experienced operator to run a company owned by two graduate students, and stayed a decade — through the IPO and the rise of the ad business. Was Sun\'s CTO before that.',
    claimToFame: 'The adult supervision, for ten years',
    experience: [
      { org: 'Google / Alphabet', role: 'CEO, then Executive Chairman', years: '2001–2017' },
      { org: 'Novell', role: 'CEO', years: '1997–2001' },
      { org: 'Sun Microsystems', role: 'CTO' },
    ],
  },
  'marissa-mayer': {
    headline: 'prev. CEO at Yahoo · Google employee #20',
    location: 'Palo Alto, USA',
    about:
      'Google\'s first female engineer and the person who guarded the famously plain search page for a decade — including the A/B test across dozens of shades of blue. Later spent five years trying to turn Yahoo around.',
    claimToFame: 'Tested forty shades of blue',
    experience: [
      { org: 'Yahoo', role: 'CEO', years: '2012–2017' },
      { org: 'Google', role: 'Engineer, then VP of Search Products', years: '1999–2012' },
    ],
  },
  'susan-wojcicki': {
    headline: 'prev. CEO at YouTube · Google employee #16',
    location: 'Los Altos, USA',
    about:
      'Rented her garage to Larry and Sergey in 1998 and then joined them. Built AdSense — the business that funded the free web — and pushed Google to buy YouTube, which she later ran for nine years.',
    claimToFame: 'Rented the garage, then built AdSense',
    experience: [
      { org: 'YouTube', role: 'CEO', years: '2014–2023' },
      { org: 'Google', role: 'SVP, Advertising & Commerce', years: '1999–2014' },
    ],
  },
  'sundar-pichai': {
    headline: 'CEO at Alphabet and Google',
    location: 'Mountain View, USA',
    about:
      'Argued internally that Google needed its own browser when nobody wanted to fight that war again, shipped Chrome, took over Android, and then the whole company.',
    claimToFame: 'Shipped Chrome, then ran everything',
    experience: [
      { org: 'Alphabet', role: 'CEO', years: '2019–' },
      { org: 'Google', role: 'Product lead for Chrome and Android, then CEO', years: '2004–' },
    ],
  },
  'jeff-dean': {
    headline: 'Chief Scientist at Google DeepMind and Google Research',
    location: 'Mountain View, USA',
    about:
      'Wrote or co-wrote most of the systems that made a search engine work at planetary scale — MapReduce, BigTable, Spanner, TensorFlow — then co-founded Google Brain. The subject of an entire genre of engineering jokes.',
    claimToFame: 'MapReduce, BigTable, Spanner, TensorFlow',
    experience: [{ org: 'Google', role: 'Senior Fellow, now Chief Scientist', years: '1999–' }],
  },
  'urs-holzle': {
    headline: 'Google Senior Fellow · first VP of Engineering at Google',
    location: 'Mountain View, USA',
    about:
      'Google\'s eighth employee, and the person who decided the company should design and build its own datacentres out of cheap machines rather than buying big ones. Everyone builds infrastructure his way now.',
    claimToFame: 'Built Google\'s datacentres from scratch',
    experience: [{ org: 'Google', role: 'First VP of Engineering, Senior Fellow', years: '1999–' }],
  },
  'salar-kamangar': {
    headline: 'prev. CEO at YouTube · Google employee #9',
    location: 'Mountain View, USA',
    about:
      'Wrote Google\'s first business plan and was central to building AdWords — the product that turned search into a money machine. Ran YouTube for four years afterwards.',
    claimToFame: 'Wrote the business plan, then AdWords',
    experience: [
      { org: 'YouTube', role: 'CEO', years: '2010–2014' },
      { org: 'Google', role: 'Early employee; AdWords', years: '1999–' },
    ],
  },

  // ── Amazon ────────────────────────────────────────────────────────────
  'jeff-bezos': {
    headline: 'Founder & Executive Chairman at Amazon · Founder at Blue Origin',
    location: 'Miami, USA',
    about:
      'Left a hedge fund to sell books online, then refused to stop expanding — into everything else, then into the infrastructure everyone else builds on. Writes a shareholder letter every year insisting it is still day one.',
    claimToFame: 'Books, then everything, then the cloud',
    experience: [
      { org: 'Amazon', role: 'Founder & CEO, now Executive Chairman', years: '1994–' },
      { org: 'Blue Origin', role: 'Founder', years: '2000–' },
      { org: 'D. E. Shaw', role: 'SVP' },
    ],
  },
  'andy-jassy': {
    headline: 'CEO at Amazon · founded AWS',
    location: 'Seattle, USA',
    about:
      'Wrote the plan for Amazon Web Services and ran it for fifteen years while it quietly became the most profitable part of the company and the substrate for most of the internet. Took over from Bezos in 2021.',
    claimToFame: 'Founded AWS',
    experience: [
      { org: 'Amazon', role: 'CEO', years: '2021–' },
      { org: 'Amazon Web Services', role: 'Founder & CEO', years: '2003–2021' },
    ],
  },
  'werner-vogels': {
    headline: 'CTO at Amazon',
    location: 'Seattle, USA',
    about:
      'Came from distributed systems research and has spent twenty years telling engineers to design for the assumption that everything fails all the time — which is roughly how the cloud ended up being built.',
    claimToFame: '"Everything fails, all the time"',
    experience: [
      { org: 'Amazon', role: 'CTO', years: '2004–' },
      { org: 'Cornell University', role: 'Research Scientist' },
    ],
  },
  'jeff-wilke': {
    headline: 'prev. CEO of Worldwide Consumer at Amazon',
    location: 'Seattle, USA',
    about:
      'Rebuilt Amazon\'s fulfilment network from a book-shipping operation into the logistics system that made two-day delivery an expectation rather than a premium.',
    claimToFame: 'Built the fulfilment network',
    experience: [{ org: 'Amazon', role: 'CEO, Worldwide Consumer', years: '1999–2021' }],
  },
  'dave-limp': {
    headline: 'CEO at Blue Origin · prev. SVP Devices & Services at Amazon',
    location: 'Seattle, USA',
    about:
      'Ran the hardware group behind Kindle, Echo and Alexa for a decade — the part of Amazon that shipped physical products on a schedule — then left to run Bezos\'s rocket company.',
    claimToFame: 'Kindle, Echo, Alexa — then rockets',
    experience: [
      { org: 'Blue Origin', role: 'CEO', years: '2023–' },
      { org: 'Amazon', role: 'SVP, Devices & Services', years: '2010–2023' },
    ],
  },

  // ── Microsoft ─────────────────────────────────────────────────────────
  'bill-gates': {
    headline: 'Co-founder at Microsoft · Co-chair at the Gates Foundation',
    location: 'Seattle, USA',
    about:
      'Dropped out of Harvard to write a BASIC interpreter, then spent twenty-five years winning every platform fight he entered — often by reading the competition\'s interfaces more carefully than they did. Has run a foundation since.',
    claimToFame: 'A PC on every desk, running his software',
    experience: [
      { org: 'Microsoft', role: 'Co-founder, CEO, Chief Software Architect', years: '1975–2008' },
      { org: 'Gates Foundation', role: 'Co-chair', years: '2000–' },
    ],
  },
  'steve-ballmer': {
    headline: 'prev. CEO at Microsoft · Owner of the LA Clippers',
    location: 'Los Angeles, USA',
    about:
      'Microsoft\'s thirtieth employee and its first business hire. Ran the company for fourteen years — enormously profitable ones, and the ones it spent missing mobile. Bought a basketball team on the way out.',
    claimToFame: 'DEVELOPERS, DEVELOPERS, DEVELOPERS',
    experience: [
      { org: 'LA Clippers', role: 'Owner', years: '2014–' },
      { org: 'Microsoft', role: 'CEO', years: '2000–2014' },
    ],
  },
  'satya-nadella': {
    headline: 'Chairman & CEO at Microsoft',
    location: 'Redmond, USA',
    about:
      'Inherited a company that had missed mobile and was widely written off, bet everything on cloud and then on a partnership with OpenAI, and added well over a trillion dollars of value doing it — mostly by being calm about it.',
    claimToFame: 'The quiet trillion-dollar turnaround',
    experience: [
      { org: 'Microsoft', role: 'CEO', years: '2014–' },
      { org: 'Microsoft', role: 'EVP, Cloud & Enterprise', years: '1992–2014' },
    ],
  },
  'paul-allen': {
    headline: 'Co-founder at Microsoft · Founder at Vulcan',
    location: 'Seattle, USA',
    about:
      'The one who spotted the Altair on a magazine cover and told Gates they had to write software for it. Left Microsoft in 1983 and spent the rest of his life on sports teams, museums and science funding.',
    claimToFame: 'Saw the Altair, started Microsoft',
    experience: [
      { org: 'Microsoft', role: 'Co-founder', years: '1975–1983' },
      { org: 'Vulcan Inc.', role: 'Founder' },
    ],
  },
  'kevin-scott': {
    headline: 'CTO at Microsoft · prev. SVP Engineering at LinkedIn',
    location: 'Redmond, USA',
    about:
      'The technical case for Microsoft betting on OpenAI, and the person who had to make the infrastructure exist afterwards. Ran engineering at LinkedIn through its acquisition first.',
    claimToFame: 'CTO of the OpenAI bet',
    experience: [
      { org: 'Microsoft', role: 'CTO', years: '2017–' },
      { org: 'LinkedIn', role: 'SVP of Engineering', years: '2011–2017' },
    ],
  },
  'charles-simonyi': {
    headline: 'prev. Chief Architect at Microsoft · created Word and Excel',
    location: 'Seattle, USA',
    about:
      'Wrote the first WYSIWYG editor at Xerox PARC, brought the idea to Microsoft, and led the teams that built Word and Excel. Later paid his own way to the International Space Station. Twice.',
    claimToFame: 'Built Word and Excel, then went to space',
    experience: [
      { org: 'Microsoft', role: 'Chief Architect', years: '1981–2002' },
      { org: 'Xerox PARC', role: 'Researcher', note: 'Wrote Bravo, the first WYSIWYG editor' },
    ],
  },
  'ray-ozzie': {
    headline: 'prev. Chief Software Architect at Microsoft · created Lotus Notes',
    location: 'Boston, USA',
    about:
      'Built Lotus Notes — collaboration software a decade and a half before anyone said "cloud" — then took over from Bill Gates as Microsoft\'s Chief Software Architect and wrote the memo that pushed the company towards services.',
    claimToFame: 'Groupware before the cloud existed',
    experience: [
      { org: 'Microsoft', role: 'Chief Software Architect', years: '2005–2010' },
      { org: 'Groove Networks', role: 'Founder', note: 'Acquired by Microsoft' },
      { org: 'Lotus', role: 'Creator of Lotus Notes' },
    ],
  },
  'anders-hejlsberg': {
    headline: 'Technical Fellow at Microsoft · created C# and TypeScript',
    location: 'Seattle, USA',
    about:
      'Wrote Turbo Pascal single-handedly, designed Delphi, then went to Microsoft and designed C# and TypeScript. Very few people have shaped how programmers actually type for this long.',
    claimToFame: 'Turbo Pascal, Delphi, C#, TypeScript',
    experience: [
      { org: 'Microsoft', role: 'Technical Fellow', years: '1996–' },
      { org: 'Borland', role: 'Chief Engineer', note: 'Turbo Pascal and Delphi' },
    ],
  },

  // ── Tesla ─────────────────────────────────────────────────────────────
  'jb-straubel': {
    headline: 'Founder & CEO at Redwood Materials · Co-founder & CTO at Tesla',
    location: 'Carson City, USA',
    about:
      'The battery was always the actual company, and he owned it — pack architecture, the Gigafactory, the charging network. Left in 2019 to build the business that recycles the batteries back into raw materials.',
    claimToFame: 'The battery was the whole company',
    experience: [
      { org: 'Redwood Materials', role: 'Founder & CEO', years: '2017–' },
      { org: 'Tesla', role: 'Co-founder & CTO', years: '2004–2019' },
    ],
  },
  'franz-von-holzhausen': {
    headline: 'Chief Designer at Tesla',
    location: 'Los Angeles, USA',
    about:
      'Has drawn every Tesla since the Model S — the car that made electric desirable rather than apologetic — and also the Cybertruck, which is the other thing entirely.',
    claimToFame: 'Drew the Model S, and the Cybertruck',
    experience: [
      { org: 'Tesla', role: 'Chief Designer', years: '2008–' },
      { org: 'Mazda', role: 'Director of Design, North America' },
    ],
  },
  'drew-baglino': {
    headline: 'prev. SVP Powertrain & Energy Engineering at Tesla',
    location: 'Palo Alto, USA',
    about:
      'Joined as a junior engineer in 2006 and ended up running powertrain and energy — the person turning battery and motor research into something that could actually be manufactured at volume.',
    claimToFame: 'Made the powertrain manufacturable',
    experience: [{ org: 'Tesla', role: 'Engineer, then SVP Powertrain & Energy', years: '2006–2024' }],
  },

  // ── Uber ──────────────────────────────────────────────────────────────
  'travis-kalanick': {
    headline: 'CEO at CloudKitchens · Co-founder at Uber',
    location: 'Los Angeles, USA',
    about:
      'Built Uber into a global company in under a decade by treating local regulation as an obstacle rather than a rule, which worked until the accumulated culture problems forced him out in 2017.',
    claimToFame: 'Growth at any cost, until the cost arrived',
    experience: [
      { org: 'CloudKitchens', role: 'CEO', years: '2018–' },
      { org: 'Uber', role: 'Co-founder & CEO', years: '2009–2017' },
      { org: 'Red Swoosh', role: 'Founder', note: 'Acquired by Akamai' },
    ],
  },
  'garrett-camp': {
    headline: 'Co-founder at Uber · Founder at StumbleUpon and Expa',
    location: 'San Francisco, USA',
    about:
      'Had the idea standing in the snow in Paris unable to get a cab, and brought in Kalanick to run it. Had already built and sold StumbleUpon before that.',
    claimToFame: 'Had the idea, hired the operator',
    experience: [
      { org: 'Expa', role: 'Founder', years: '2013–' },
      { org: 'Uber', role: 'Co-founder', years: '2009–' },
      { org: 'StumbleUpon', role: 'Co-founder & CEO', years: '2002–2012' },
    ],
  },
  'ryan-graves': {
    headline: 'prev. SVP of Global Operations at Uber · Uber employee #1',
    location: 'Chicago, USA',
    about:
      'Replied to a tweet asking for help and became Uber\'s first employee — briefly its CEO, then the person who built the city-by-city operations playbook the company launched with everywhere.',
    claimToFame: 'Got the job by replying to a tweet',
    experience: [{ org: 'Uber', role: 'First employee, CEO, then SVP Global Operations', years: '2010–2017' }],
  },
  'emil-michael': {
    headline: 'prev. SVP of Business at Uber',
    location: 'San Francisco, USA',
    about:
      'Ran deals and fundraising during Uber\'s fastest years. Resigned in 2017; best remembered for floating the idea of digging up dirt on journalists who covered the company critically.',
    claimToFame: 'Suggested opposition research on reporters',
    experience: [
      { org: 'Uber', role: 'SVP of Business', years: '2013–2017' },
      { org: 'Klout', role: 'COO' },
    ],
  },
  'thuan-pham': {
    headline: 'prev. CTO at Uber',
    location: 'San Francisco, USA',
    about:
      'Arrived in the United States as a refugee from Vietnam, and later spent seven years keeping Uber\'s systems standing while usage grew by orders of magnitude a year.',
    claimToFame: 'Kept it up through 10x, repeatedly',
    experience: [
      { org: 'Uber', role: 'CTO', years: '2013–2020' },
      { org: 'VMware', role: 'VP of Engineering' },
    ],
  },

  // ── Twitter / X ───────────────────────────────────────────────────────
  'jack-dorsey': {
    headline: 'Co-founder at Twitter · Founder & Chairman at Block',
    location: 'San Francisco, USA',
    about:
      'Co-founded Twitter, was pushed out, came back as CEO, and ran it alongside a payments company at the same time. Left in 2021 and now spends his energy on bitcoin and decentralised protocols.',
    claimToFame: 'Ran Twitter and Square simultaneously',
    experience: [
      { org: 'Block (Square)', role: 'Founder & Chairman', years: '2009–' },
      { org: 'Twitter', role: 'Co-founder & CEO', years: '2006–2008, 2015–2021' },
    ],
  },
  'ev-williams': {
    headline: 'Co-founder at Twitter · Founder at Blogger and Medium',
    location: 'San Francisco, USA',
    about:
      'Has now founded three separate platforms for publishing text on the internet — Blogger, Twitter and Medium — and sold or led each one.',
    claimToFame: 'Blogger, Twitter, Medium',
    experience: [
      { org: 'Medium', role: 'Founder', years: '2012–' },
      { org: 'Twitter', role: 'Co-founder & CEO', years: '2006–2010' },
      { org: 'Blogger / Pyra Labs', role: 'Co-founder', note: 'Acquired by Google' },
    ],
  },
  'biz-stone': {
    headline: 'Co-founder at Twitter · Co-founder at Medium',
    location: 'San Francisco, USA',
    about:
      'The co-founder who did the tone: the voice of the product, the fail whale era, and the argument that a social network should feel like a good place to be.',
    claimToFame: 'Set the voice of early Twitter',
    experience: [
      { org: 'Twitter', role: 'Co-founder', years: '2006–2011' },
      { org: 'Blogger / Google', role: 'Creative director' },
    ],
  },
  'noah-glass': {
    headline: 'Co-founder at Odeo · named Twitter',
    location: 'San Francisco, USA',
    about:
      'Ran the podcasting startup the idea came out of, and gave Twitter its name. Was pushed out early and left off most of the founding story for years.',
    claimToFame: 'Named it, then got written out',
    experience: [{ org: 'Odeo / Twitter', role: 'Co-founder', years: '2005–2006' }],
  },
  'jason-goldman': {
    headline: 'prev. VP of Product at Twitter · prev. Chief Digital Officer at the White House',
    location: 'San Francisco, USA',
    about:
      'Ran product at Twitter through its messiest growth years, having done the same at Blogger, and later took the digital job in the Obama White House.',
    claimToFame: 'The adult in the product room',
    experience: [
      { org: 'The White House', role: 'Chief Digital Officer', years: '2015–2017' },
      { org: 'Twitter', role: 'VP of Product', years: '2007–2010' },
      { org: 'Blogger / Google', role: 'Product manager' },
    ],
  },
  'dick-costolo': {
    headline: 'prev. CEO at Twitter · Founder at FeedBurner',
    location: 'San Francisco, USA',
    about:
      'Did improv comedy at Second City before becoming a founder, sold FeedBurner to Google, then ran Twitter for five years — through the IPO and the growth problem nobody could solve.',
    claimToFame: 'Improv comedian to Twitter CEO',
    experience: [
      { org: 'Twitter', role: 'CEO', years: '2010–2015', note: 'NYSE IPO, 2013' },
      { org: 'FeedBurner', role: 'Co-founder & CEO', note: 'Acquired by Google' },
    ],
  },
  'parag-agrawal': {
    headline: 'prev. CEO at Twitter',
    location: 'San Francisco, USA',
    about:
      'Rose from engineer to CTO to CEO, took the job in late 2021, and was fired within minutes of Musk completing the acquisition eleven months later.',
    claimToFame: 'CEO for eleven months',
    experience: [{ org: 'Twitter', role: 'Engineer, CTO, then CEO', years: '2011–2022' }],
  },
  'linda-yaccarino': {
    headline: 'prev. CEO at X · prev. Chairman of Global Advertising at NBCUniversal',
    location: 'New York, USA',
    about:
      'Ran advertising sales at NBCUniversal for over a decade, then took the hardest advertising job in the industry: persuading brands to come back to a platform whose owner kept telling them not to.',
    claimToFame: 'Sent to win the advertisers back',
    experience: [
      { org: 'X', role: 'CEO', years: '2023–2025' },
      { org: 'NBCUniversal', role: 'Chairman, Global Advertising & Partnerships', years: '2011–2023' },
    ],
  },

  // ── Airbnb ────────────────────────────────────────────────────────────
  'brian-chesky': {
    headline: 'Co-founder & CEO at Airbnb',
    location: 'San Francisco, USA',
    about:
      'An industrial designer by training who runs the company like a design studio, down to reviewing releases himself. Took Airbnb public in 2020, months after the pandemic wiped out most of its bookings.',
    claimToFame: 'IPO\'d the year travel stopped',
    experience: [
      { org: 'Airbnb', role: 'Co-founder & CEO', years: '2008–', note: 'Nasdaq IPO, 2020' },
      { org: 'Rhode Island School of Design', role: 'BFA, Industrial Design' },
    ],
  },
  'joe-gebbia': {
    headline: 'Co-founder at Airbnb · Founder at Samara',
    location: 'San Francisco, USA',
    about:
      'Design-school roommate of Chesky and the other half of the original idea — air mattresses during a sold-out conference. Funded the early company by selling novelty breakfast cereal during an election.',
    claimToFame: 'Funded it by selling election cereal',
    experience: [
      { org: 'Samara', role: 'Founder', years: '2022–' },
      { org: 'Airbnb', role: 'Co-founder', years: '2008–2022' },
    ],
  },
  'nathan-blecharczyk': {
    headline: 'Co-founder & Chief Strategy Officer at Airbnb',
    location: 'San Francisco, USA',
    about:
      'The engineer among two designers — he wrote the original site, and later built the international expansion. Had already run a profitable software business while still at school.',
    claimToFame: 'The only founder who could code',
    experience: [
      { org: 'Airbnb', role: 'Co-founder & CTO, now Chief Strategy Officer', years: '2008–' },
    ],
  },
  'belinda-johnson': {
    headline: 'prev. COO at Airbnb · prev. General Counsel at Yahoo',
    location: 'San Francisco, USA',
    about:
      'Airbnb\'s first executive hire, brought in to handle the fact that the product was illegal in half its markets. Spent a decade turning city-by-city fights into negotiated agreements.',
    claimToFame: 'Talked the cities down, one at a time',
    experience: [
      { org: 'Airbnb', role: 'Chief Business Affairs & Legal Officer, then COO', years: '2011–2020' },
      { org: 'Yahoo', role: 'General Counsel' },
    ],
  },
  'jonathan-mildenhall': {
    headline: 'prev. CMO at Airbnb · prev. VP Global Creative at Coca-Cola',
    location: 'San Francisco, USA',
    about:
      'Came from Coca-Cola and rebuilt Airbnb around "Belong Anywhere" — the campaign that reframed renting a stranger\'s spare room as something aspirational rather than merely cheap.',
    claimToFame: '"Belong Anywhere"',
    experience: [
      { org: 'Airbnb', role: 'Chief Marketing Officer', years: '2014–2017' },
      { org: 'Coca-Cola', role: 'VP, Global Creative' },
    ],
  },

  // ── Chips & hardware ──────────────────────────────────────────────────
  'jensen-huang': {
    headline: 'Co-founder & CEO at Nvidia',
    location: 'Santa Clara, USA',
    about:
      'Started a graphics chip company in a Denny\'s in 1993 and has run it ever since. Spent fifteen years building software nobody needed for hardware nobody was buying for AI — and then everybody was.',
    claimToFame: 'Built CUDA a decade before anyone needed it',
    experience: [
      { org: 'Nvidia', role: 'Co-founder & CEO', years: '1993–' },
      { org: 'LSI Logic', role: 'Director' },
    ],
  },
  'lisa-su': {
    headline: 'Chair & CEO at AMD',
    location: 'Austin, USA',
    about:
      'Took over a chip company that was close to insolvent and, over a decade of unglamorous execution on the Zen architecture, took it from irrelevant to beating Intel outright.',
    claimToFame: 'The turnaround nobody thought possible',
    experience: [
      { org: 'AMD', role: 'CEO', years: '2014–' },
      { org: 'Freescale Semiconductor', role: 'CTO' },
      { org: 'IBM', role: 'Semiconductor R&D' },
    ],
  },
  'morris-chang': {
    headline: 'Founder at TSMC',
    location: 'Hsinchu, Taiwan',
    about:
      'Spent twenty-five years at Texas Instruments, then founded TSMC at 55 on an idea nobody had tried: a chip factory that designs nothing and competes with none of its customers. The entire fabless industry exists because of it.',
    claimToFame: 'Invented the pure-play foundry',
    experience: [
      { org: 'TSMC', role: 'Founder & CEO', years: '1987–2018' },
      { org: 'Texas Instruments', role: 'Group VP', years: '1958–1983' },
    ],
  },
  'pat-gelsinger': {
    headline: 'prev. CEO at Intel · prev. CEO at VMware',
    location: 'Santa Clara, USA',
    about:
      'Joined Intel at eighteen, became its first CTO, was the lead architect of the 486, left, ran VMware for nine years, and came back to try to fix Intel\'s manufacturing. Departed in 2024 with the turnaround unfinished.',
    claimToFame: 'Architected the 486, then came back for Intel',
    experience: [
      { org: 'Intel', role: 'CEO', years: '2021–2024' },
      { org: 'VMware', role: 'CEO', years: '2012–2021' },
      { org: 'Intel', role: 'First CTO; lead architect, 80486', years: '1979–2009' },
    ],
  },
  'jim-keller': {
    headline: 'CEO at Tenstorrent',
    location: 'Toronto, Canada',
    about:
      'Has designed a career-defining chip at four separate companies — AMD\'s K8 and Zen, Apple\'s A4 and A5, Tesla\'s Autopilot silicon — and left shortly after each one, usually before it shipped.',
    claimToFame: 'A landmark chip at four companies',
    experience: [
      { org: 'Tenstorrent', role: 'CEO', years: '2021–' },
      { org: 'Intel', role: 'SVP, Silicon Engineering', years: '2018–2020' },
      { org: 'Tesla', role: 'VP of Autopilot Hardware', years: '2016–2018' },
      { org: 'Apple', role: 'Chip architect', note: 'A4 and A5' },
    ],
  },
  'hock-tan': {
    headline: 'President & CEO at Broadcom',
    location: 'Palo Alto, USA',
    about:
      'Built one of the largest semiconductor companies in the world almost entirely by acquisition, and by being unsentimental about what happens to the acquired products and their prices afterwards.',
    claimToFame: 'Acquire, cut, then raise the price',
    experience: [
      { org: 'Broadcom', role: 'President & CEO', years: '2006–', note: 'Acquired VMware in 2023' },
    ],
  },
  'palmer-luckey': {
    headline: 'Founder at Anduril · Founder at Oculus',
    location: 'Irvine, USA',
    about:
      'Built VR headsets in his parents\' garage as a teenager, funded the first one on Kickstarter, and sold Oculus to Facebook for $2B at 21. Left in 2017 and founded a defence technology company instead.',
    claimToFame: 'Garage headset to a $2B sale at 21',
    experience: [
      { org: 'Anduril Industries', role: 'Founder', years: '2017–' },
      { org: 'Oculus VR', role: 'Founder', years: '2012–2017', note: 'Acquired by Facebook, $2B' },
    ],
  },

  // ── Crypto ────────────────────────────────────────────────────────────
  'vitalik-buterin': {
    headline: 'Co-founder at Ethereum',
    location: 'Singapore',
    about:
      'Wrote the Ethereum whitepaper at nineteen, having argued in Bitcoin Magazine that a blockchain should be able to run arbitrary programs rather than just move coins. Still the field\'s most-cited voice, and conspicuously uninterested in getting rich from it.',
    claimToFame: 'Wrote the Ethereum whitepaper at 19',
    experience: [
      { org: 'Ethereum', role: 'Co-founder', years: '2013–' },
      { org: 'Bitcoin Magazine', role: 'Co-founder & writer', years: '2011–2014' },
    ],
  },
  'brian-armstrong': {
    headline: 'Co-founder & CEO at Coinbase',
    location: 'San Francisco, USA',
    about:
      'Chose to build the regulated, boring, American version of a crypto exchange while competitors went offshore — which looked like leaving money on the table right up until it very much did not.',
    claimToFame: 'Chose regulation on purpose',
    experience: [
      { org: 'Coinbase', role: 'Co-founder & CEO', years: '2012–', note: 'Nasdaq direct listing, 2021' },
      { org: 'Airbnb', role: 'Software engineer' },
    ],
  },
  'anatoly-yakovenko': {
    headline: 'Co-founder at Solana',
    location: 'San Francisco, USA',
    about:
      'Spent twelve years on wireless protocols at Qualcomm, then applied that thinking to blockchains: use a verifiable clock so nodes stop wasting time agreeing what time it is. That trick is what makes Solana fast.',
    claimToFame: 'Proof of history',
    experience: [
      { org: 'Solana Labs', role: 'Co-founder & CEO', years: '2017–' },
      { org: 'Qualcomm', role: 'Senior Staff Engineer', years: '2003–2016' },
    ],
  },
  'michael-saylor': {
    headline: 'Executive Chairman at Strategy (MicroStrategy)',
    location: 'Miami, USA',
    about:
      'Ran a business intelligence company for thirty years, then in 2020 converted its treasury — and eventually its whole identity — into a leveraged bet on bitcoin, and has never once suggested selling.',
    claimToFame: 'Turned a software company into a bitcoin fund',
    experience: [
      { org: 'Strategy (MicroStrategy)', role: 'Co-founder, CEO, now Executive Chairman', years: '1989–' },
    ],
  },
  'changpeng-zhao': {
    headline: 'Founder at Binance',
    location: 'Dubai, UAE',
    about:
      'Built the largest crypto exchange in the world in under a year, with no fixed headquarters anywhere — a structure that was the whole advantage until it was the whole problem. Pleaded guilty to a US anti-money-laundering charge in 2023, stepped down, and served four months.',
    claimToFame: 'Biggest exchange, no headquarters',
    experience: [
      { org: 'Binance', role: 'Founder & CEO', years: '2017–2023' },
      { org: 'OKCoin', role: 'CTO' },
    ],
  },
  'sam-bankman-fried': {
    headline: 'Founder at FTX and Alameda Research',
    location: 'USA',
    about:
      'Ran a crypto exchange and a trading firm at the same time, moved customer money between them, and became the movement\'s public face — congressional testimony, stadium naming rights, effective altruism — until FTX collapsed in November 2022. Convicted of fraud in 2023 and sentenced to 25 years.',
    claimToFame: 'The largest crypto collapse on record',
    experience: [
      { org: 'FTX', role: 'Founder & CEO', years: '2019–2022' },
      { org: 'Alameda Research', role: 'Founder', years: '2017–2022' },
      { org: 'Jane Street', role: 'Trader' },
    ],
  },
  'do-kwon': {
    headline: 'Co-founder at Terraform Labs',
    location: 'USA',
    about:
      'Built an algorithmic stablecoin that held its peg through pure market incentives, and mocked his critics loudly while it worked. It unravelled in days in May 2022, erasing tens of billions. Arrested in Montenegro and extradited to the United States to face fraud charges.',
    claimToFame: 'The stablecoin that wasn\'t',
    experience: [
      { org: 'Terraform Labs', role: 'Co-founder & CEO', years: '2018–2022' },
      { org: 'Microsoft', role: 'Software engineer' },
    ],
  },
  'su-zhu': {
    headline: 'Co-founder at Three Arrows Capital',
    location: 'Singapore',
    about:
      'Ran one of the most leveraged funds in crypto on an openly stated thesis that the cycle would never turn down again. It turned down in 2022; the fund went into liquidation owing billions to nearly every lender in the industry.',
    claimToFame: 'The supercycle that ended',
    experience: [
      { org: 'Three Arrows Capital', role: 'Co-founder & CEO', years: '2012–2022' },
      { org: 'Credit Suisse', role: 'Trader' },
    ],
  },

  // ── Spectacular implosions ────────────────────────────────────────────
  'adam-neumann': {
    headline: 'Founder & CEO at Flow · Co-founder at WeWork',
    location: 'Miami, USA',
    about:
      'Raised billions to rent office space at scale while describing it as elevating the world\'s consciousness. The IPO filing in 2019 exposed the economics and the governance, the listing was pulled, and he was paid a fortune to leave. Now running a residential property company.',
    claimToFame: 'The IPO filing that ended the company',
    experience: [
      { org: 'Flow', role: 'Founder & CEO', years: '2022–' },
      { org: 'WeWork', role: 'Co-founder & CEO', years: '2010–2019' },
    ],
  },
  'elizabeth-holmes': {
    headline: 'Founder at Theranos',
    location: 'USA',
    about:
      'Dropped out of Stanford at nineteen promising blood tests from a single finger prick, and assembled a board of former secretaries of state around a technology that never worked. Convicted of defrauding investors in 2022 and sentenced to over eleven years.',
    claimToFame: 'One drop of blood, none of it real',
    experience: [{ org: 'Theranos', role: 'Founder & CEO', years: '2003–2018' }],
  },
  'martin-shkreli': {
    headline: 'prev. CEO at Turing Pharmaceuticals',
    location: 'New York, USA',
    about:
      'Raised the price of a decades-old antiparasitic drug from $13.50 to $750 overnight and then argued about it online for a year. Convicted of securities fraud in a separate matter in 2017, served time, and was banned from the pharmaceutical industry for life.',
    claimToFame: '$13.50 to $750, overnight',
    experience: [
      { org: 'Turing Pharmaceuticals', role: 'Founder & CEO', years: '2015–2016' },
      { org: 'Retrophin', role: 'Founder & CEO', years: '2011–2014' },
    ],
  },
  'trevor-milton': {
    headline: 'Founder at Nikola',
    location: 'Utah, USA',
    about:
      'Took an electric truck company public at a valuation above Ford\'s on the strength of a promotional video that turned out to show the truck rolling down a hill rather than driving. Convicted of fraud in 2022.',
    claimToFame: 'The truck was rolling downhill',
    experience: [{ org: 'Nikola Corporation', role: 'Founder & Executive Chairman', years: '2014–2020' }],
  },
  'meg-whitman': {
    headline: 'prev. CEO at eBay, HPE and Quibi · prev. US Ambassador to Kenya',
    location: 'Atherton, USA',
    about:
      'Took eBay from thirty employees to fifteen thousand, then ran Hewlett-Packard through its split. Her last operating job was Quibi, the short-form video service that raised $1.75B and shut down in six months.',
    claimToFame: 'Ran eBay and HP, then Quibi',
    experience: [
      { org: 'Quibi', role: 'CEO', years: '2018–2020' },
      { org: 'Hewlett Packard Enterprise', role: 'CEO', years: '2011–2018' },
      { org: 'eBay', role: 'CEO', years: '1998–2008' },
    ],
  },
  'masayoshi-son': {
    headline: 'Founder, Chairman & CEO at SoftBank',
    location: 'Tokyo, Japan',
    about:
      'Put $20M into Alibaba in 2000 and turned it into one of the best venture returns in history, then built a $100B fund and wrote cheques large enough to distort entire markets. Talks in terms of a three-hundred-year plan and decides in minutes.',
    claimToFame: '$20M into Alibaba, worth tens of billions',
    experience: [
      { org: 'SoftBank Group', role: 'Founder & CEO', years: '1981–' },
      { org: 'SoftBank Vision Fund', role: 'Founder', years: '2017–' },
    ],
  },

  // ── Dot-com era ───────────────────────────────────────────────────────
  'marc-andreessen': {
    headline: 'Co-founder & General Partner at Andreessen Horowitz · Co-founder at Netscape',
    location: 'Atherton, USA',
    about:
      'Co-wrote Mosaic as a student, turned it into Netscape, and gave most people their first look at the web. Now runs one of the largest venture firms and writes the essays that set the industry\'s arguments.',
    claimToFame: 'Wrote the first popular web browser',
    experience: [
      { org: 'Andreessen Horowitz', role: 'Co-founder & General Partner', years: '2009–' },
      { org: 'Opsware / Loudcloud', role: 'Co-founder', years: '1999–2007', note: 'Acquired by HP, $1.6B' },
      { org: 'Netscape', role: 'Co-founder', years: '1994–1999' },
    ],
  },
  'ben-horowitz': {
    headline: 'Co-founder & General Partner at Andreessen Horowitz',
    location: 'Atherton, USA',
    about:
      'Ran Loudcloud through the dot-com crash — down to a share price of thirty-five cents — sold it to HP, and then wrote the book about what that actually felt like, which is now the standard text on the subject.',
    claimToFame: 'Wrote The Hard Thing About Hard Things',
    experience: [
      { org: 'Andreessen Horowitz', role: 'Co-founder & General Partner', years: '2009–' },
      { org: 'Opsware / Loudcloud', role: 'Co-founder & CEO', years: '1999–2007' },
      { org: 'Netscape', role: 'VP of Product' },
    ],
  },
  'jerry-yang': {
    headline: 'Co-founder at Yahoo · Founding Partner at AME Cloud Ventures',
    location: 'Palo Alto, USA',
    about:
      'Built a hand-curated directory of the web as a Stanford student and turned it into the front page of the internet. Turned down Microsoft\'s $44.6B offer in 2008; also made the Alibaba investment that ended up worth more than Yahoo itself.',
    claimToFame: 'Said no to Microsoft\'s $44.6B',
    experience: [
      { org: 'AME Cloud Ventures', role: 'Founding Partner', years: '2012–' },
      { org: 'Yahoo', role: 'Co-founder; CEO', years: '1995–2012' },
    ],
  },
  'mark-cuban': {
    headline: 'Owner of the Dallas Mavericks · Founder at Cost Plus Drugs',
    location: 'Dallas, USA',
    about:
      'Sold Broadcast.com to Yahoo for $5.7B in 1999 and hedged the stock immediately, which is why he kept the money when the market collapsed. Bought a basketball team, did television, and now sells generic drugs at cost.',
    claimToFame: 'Sold at the exact top, and hedged it',
    experience: [
      { org: 'Cost Plus Drugs', role: 'Co-founder', years: '2022–' },
      { org: 'Dallas Mavericks', role: 'Owner', years: '2000–' },
      { org: 'Broadcast.com', role: 'Co-founder', years: '1995–1999', note: 'Acquired by Yahoo, $5.7B' },
    ],
  },
  'steve-case': {
    headline: 'Chairman & CEO at Revolution · Co-founder at AOL',
    location: 'Washington DC, USA',
    about:
      'Got most of America onto the internet by mailing them a CD-ROM, then merged AOL with Time Warner in what is still widely cited as the worst deal in corporate history.',
    claimToFame: 'Mailed everyone a CD-ROM',
    experience: [
      { org: 'Revolution', role: 'Chairman & CEO', years: '2005–' },
      { org: 'AOL', role: 'Co-founder & CEO', years: '1985–2003' },
    ],
  },
  'jawed-karim': {
    headline: 'Co-founder at YouTube · prev. engineer at PayPal',
    location: 'San Francisco, USA',
    about:
      'One of three PayPal engineers who started YouTube, and the one who uploaded the first video to it — nineteen seconds at the San Diego Zoo. Left early to go back to graduate school.',
    claimToFame: 'Uploaded the first video to YouTube',
    experience: [
      { org: 'YouTube', role: 'Co-founder', years: '2005–2006' },
      { org: 'PayPal', role: 'Software engineer' },
    ],
  },

  // ── Investors & essayists ─────────────────────────────────────────────
  'paul-graham': {
    headline: 'Co-founder at Y Combinator · Co-founder at Viaweb',
    location: 'England, UK',
    about:
      'Built the first web-based store builder, sold it to Yahoo, and then invented the modern seed accelerator — small cheques, batches, three months. The essays did as much as the money did.',
    claimToFame: 'Invented the startup accelerator',
    experience: [
      { org: 'Y Combinator', role: 'Co-founder', years: '2005–2014' },
      { org: 'Viaweb', role: 'Co-founder', years: '1995–1998', note: 'Acquired by Yahoo' },
    ],
  },
  'naval-ravikant': {
    headline: 'Co-founder at AngelList',
    location: 'San Francisco, USA',
    about:
      'Built the platform that made angel investing a process rather than a network, invested early in a long list of the companies that mattered, and became better known for compressing all of it into aphorisms.',
    claimToFame: 'Made angel investing a platform',
    experience: [
      { org: 'AngelList', role: 'Co-founder & Chairman', years: '2010–' },
      { org: 'Epinions', role: 'Co-founder' },
    ],
  },
  'chamath-palihapitiya': {
    headline: 'Founder & CEO at Social Capital · prev. VP of Growth at Facebook',
    location: 'Palo Alto, USA',
    about:
      'Ran the growth team at Facebook during its fastest years, then built a fund and became the loudest promoter of the 2020–21 SPAC wave — taking a string of companies public through blank-cheque vehicles, most of which fell hard afterwards.',
    claimToFame: 'Face of the SPAC boom',
    experience: [
      { org: 'Social Capital', role: 'Founder & CEO', years: '2011–' },
      { org: 'Facebook', role: 'VP of Growth', years: '2007–2011' },
      { org: 'AOL', role: 'VP, Instant Messaging' },
    ],
  },
  'bill-gurley': {
    headline: 'prev. General Partner at Benchmark',
    location: 'Austin, USA',
    about:
      'Backed Uber, OpenTable and Zillow, sat on the boards through the hard parts, and spent years publicly warning about late-stage valuations and the cost of the traditional IPO process while everyone else was celebrating.',
    claimToFame: 'Warned about the bubble from inside it',
    experience: [
      { org: 'Benchmark', role: 'General Partner', years: '1999–2020' },
      { org: 'Hummer Winblad', role: 'Partner' },
    ],
  },
  'nat-friedman': {
    headline: 'prev. CEO at GitHub · Co-founder at Xamarin and Ximian',
    location: 'San Francisco, USA',
    about:
      'Spent a career in open source, sold two companies to Microsoft, and then ran GitHub after Microsoft bought that too — the acquisition developers were braced to hate and mostly ended up fine with. Copilot shipped on his watch.',
    claimToFame: 'Ran GitHub after Microsoft bought it',
    experience: [
      { org: 'GitHub', role: 'CEO', years: '2018–2021', note: 'Shipped Copilot' },
      { org: 'Xamarin', role: 'Co-founder & CEO', years: '2011–2016', note: 'Acquired by Microsoft' },
      { org: 'Ximian', role: 'Co-founder', note: 'Acquired by Novell' },
    ],
  },
  'andrew-chen': {
    headline: 'General Partner at Andreessen Horowitz · author of The Cold Start Problem',
    location: 'San Francisco, USA',
    about:
      'Ran growth at Uber, then wrote the book on the hardest part of any network product — getting it to work when nobody is using it yet.',
    claimToFame: 'Wrote the book on the cold start',
    experience: [
      { org: 'Andreessen Horowitz', role: 'General Partner', years: '2018–' },
      { org: 'Uber', role: 'Head of Rider Growth', years: '2015–2018' },
    ],
  },
};

const PILLAR_LABELS: Record<string, string> = {
  product: 'product',
  engineering: 'engineering',
  growth: 'growth',
  execution: 'execution',
  fundraising: 'fundraising',
};

const bestRole = (person: Person) =>
  ROLES.reduce((best, role) =>
    affinityFor(person, role) > affinityFor(person, best) ? role : best,
  );

const chaosLine = (chaos: number) =>
  chaos >= 70
    ? 'Brings a lot of chaos with them.'
    : chaos <= 30
      ? 'Very low drama.'
      : 'Manageable amount of drama.';

/**
 * The card for someone with no hand-written entry. Every sentence restates
 * something the game already knows about them, so it can't be wrong.
 *
 * Only tier A cohorts contribute an Experience row: those are real teams at
 * real moments, so "Apple · iPhone team · 2007" is a job. Every other tier is a
 * themed draw — nobody has ever worked at "Almost Changed The World" — and
 * listing one as an employer would be inventing a fact, so the section is
 * dropped instead.
 */
export function derivedBio(person: Person, cohort: Cohort): PersonBio {
  const [first, second] = [...CORE_PILLARS].sort((a, b) => person.stats[b] - person.stats[a]);
  const seat = bestRole(person);
  const realTeam = cohort.tier === 'A';
  return {
    headline: realTeam ? `${person.tagline} · ${cohort.name} ${cohort.era}` : person.tagline,
    about: `Drafts best as ${ROLE_LABELS[seat]}. Strongest at ${PILLAR_LABELS[first]} and ${PILLAR_LABELS[second]}. ${chaosLine(person.stats.chaos)}`,
    claimToFame: person.tagline,
    experience: realTeam ? [{ org: cohort.name, role: ROLE_LABELS[seat], years: cohort.era }] : [],
  };
}

/** The written bio if there is one, otherwise one assembled from game data. */
export const bioFor = (person: Person, cohort: Cohort): PersonBio =>
  BIOS[person.id] ?? derivedBio(person, cohort);

/** Whether this person's card is hand-written rather than derived. */
export const hasWrittenBio = (id: PersonId): boolean => BIOS[id] !== undefined;
